import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Search, Globe, Instagram, User, LogOut, MapPin, Shield, LayoutGrid, List, ChevronUp, ChevronDown, Image, X, ChevronLeft, ChevronRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = "";

// Membership type badge colors
const typeColors = {
  artist: 'bg-purple-100 text-purple-800',
  patron: 'bg-blue-100 text-blue-800',
  instructor: 'bg-green-100 text-green-800',
  board: 'bg-cafa-red-100 text-cafa-red',
  general: 'bg-stone-100 text-stone-600'
};

// Default column widths for list view
const DEFAULT_COLUMN_WIDTHS = {
  name: 200,
  type: 90,
  location: 140,
  gallery: 80,
  website: 160,
  instagram: 120
};

export const MemberDirectory = () => {
  const { user, token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortField, setSortField] = useState('last_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const [resizing, setResizing] = useState(null);
  const [galleryModal, setGalleryModal] = useState(null); // { member, gallery }
  const [viewingImage, setViewingImage] = useState(null);
  const [fullGallery, setFullGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const tableRef = useRef(null);
  const navigate = useNavigate();
  
  // Directory filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [hasGallery, setHasGallery] = useState(false);
  
  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);

  const canReviewGallery = isAdmin || (user?.gallery_roles || []).some(role => ['reviewer', 'coordinator', 'publisher'].includes(role));

  // Normalize state abbreviations so TX / Texas / TEXAS all match
  const normalizeState = (s) => {
    if (!s) return '';
    const map = {
      'TEXAS': 'TX', 'Texas': 'TX', 'texas': 'TX', 'tx': 'TX', 'Tx': 'TX', 'TX - Texas': 'TX',
      'FLORIDA': 'FL', 'Florida': 'FL', 'Fl': 'FL',
      'CALIFORNIA': 'CA', 'California': 'CA',
      'COLORADO': 'CO', 'Colorado': 'CO',
      'KENTUCKY': 'KY', 'Kentucky': 'KY',
      'MINNESOTA': 'MN', 'Minnesota': 'MN',
      'MONTANA': 'MT', 'Montana': 'MT',
      'OREGON': 'OR', 'Oregon': 'OR',
      'UTAH': 'UT', 'Utah': 'UT',
      'WASHINGTON': 'WA', 'Washington': 'WA',
      'ne': 'NE',
    };
    return map[s] || s.toUpperCase().trim();
  };

  // Column resize handlers
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] });
  }, [columnWidths]);

  const handleMouseMove = useCallback((e) => {
    if (!resizing) return;
    const diff = e.clientX - resizing.startX;
    const newWidth = Math.max(60, resizing.startWidth + diff);
    setColumnWidths(prev => ({ ...prev, [resizing.columnKey]: newWidth }));
  }, [resizing]);

  const stopResize = useCallback(() => {
    setResizing(null);
  }, []);

  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', stopResize);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', stopResize);
      };
    }
  }, [resizing, handleMouseMove, stopResize]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort icon based on current sort state
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronDown size={14} className="text-stone-300" />;
    }
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/members/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch members (public directory only - active and lifetime members)
  useEffect(() => {
    const fetchMembers = async () => {
      if (!token) return;
      setLoading(true);
      
      try {
        const response = await fetch(`${API_URL}/api/members/directory`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setMembers(data);
        }
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchMembers();
    }
  }, [token]);

  // Derived: unique normalized states from loaded members
  const uniqueStates = [...new Set(members.map(m => normalizeState(m.state)).filter(Boolean))].sort();

  // Filter and sort members
  const filteredMembers = members
    .filter(member => {
      const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
      const city = (member.city || '').toLowerCase();
      const instagram = (member.instagram || '').toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
             city.includes(searchTerm.toLowerCase()) ||
             instagram.includes(searchTerm.toLowerCase().replace('@', ''));

      // Type filter
      const isLifetime = member.membership_status === 'lifetime' || member.is_lifetime_member === true || member.lifetime_member === true;
      let matchesType = true;
      if (typeFilter !== 'all') {
        if (typeFilter === 'lifetime') {
          matchesType = isLifetime;
        } else {
          matchesType = !isLifetime && member.membership_type === typeFilter;
        }
      }

      // Location filter
      const memberState = normalizeState(member.state);
      const matchesLocation = locationFilter === 'all' || memberState === locationFilter;

      // Gallery filter
      const matchesGallery = !hasGallery || (member.gallery_count && member.gallery_count > 0);

      return matchesSearch && matchesType && matchesLocation && matchesGallery;
    })
    .sort((a, b) => {
      let aVal = '';
      let bVal = '';
      
      if (sortField === 'last_name') {
        aVal = `${a.last_name} ${a.first_name}`.toLowerCase();
        bVal = `${b.last_name} ${b.first_name}`.toLowerCase();
      } else if (sortField === 'membership_type') {
        const typeOrder = { 'board': 1, 'instructor': 2, 'artist': 3, 'patron': 4, 'general': 5 };
        aVal = typeOrder[a.membership_type] || 6;
        bVal = typeOrder[b.membership_type] || 6;
      } else if (sortField === 'location') {
        aVal = `${a.state || ''} ${a.city || ''}`.toLowerCase();
        bVal = `${b.state || ''} ${b.city || ''}`.toLowerCase();
      } else {
        aVal = (a[sortField] || '').toString().toLowerCase();
        bVal = (b[sortField] || '').toString().toLowerCase();
      }
      
      // Handle empty values - push to end
      if (!aVal && bVal) return sortDirection === 'asc' ? 1 : -1;
      if (aVal && !bVal) return sortDirection === 'asc' ? -1 : 1;
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Get full image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  // Open gallery modal for a member
  const openGalleryModal = async (member) => {
    setGalleryModal(member);
    setGalleryLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/members/gallery/${member.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFullGallery(data.gallery || []);
      } else {
        // Fall back to preview if full gallery not available
        setFullGallery(member.gallery_preview || []);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setFullGallery(member.gallery_preview || []);
    } finally {
      setGalleryLoading(false);
    }
  };

  // Navigate in lightbox viewer
  const navigateViewer = (direction) => {
    if (!viewingImage || fullGallery.length === 0) return;
    const currentIndex = fullGallery.findIndex(img => img.id === viewingImage.id);
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % fullGallery.length;
    } else {
      newIndex = (currentIndex - 1 + fullGallery.length) % fullGallery.length;
    }
    setViewingImage(fullGallery[newIndex]);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="member-directory-page">
      <SEOHead
        title="Member Directory"
        description="Browse the artist member directory of The Coppini Academy of Fine Art. View portfolios, specialties, and gallery work by member artists."
        canonicalPath="/members/directory"
      />
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-8 border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-serif text-stone-900">Member Area</h1>
              <p className="text-stone-600 mt-1">Welcome, {user?.first_name}!</p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {isAdmin && (
                <Link to="/admin/content">
                  <Button variant="ghost" className="text-cafa-red">
                    <Shield size={16} className="mr-1" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/members/directory">
                <Button variant="ghost" className="text-cafa-red">
                  Member Directory
                </Button>
              </Link>
              <Link to="/members/profile">
                <Button variant="ghost" className="text-stone-600">
                  My Profile
                </Button>
              </Link>
              <Link to="/members/gallery" data-testid="member-directory-gallery-link">
                <Button variant="ghost" className="text-stone-600">Gallery Applications</Button>
              </Link>
              {canReviewGallery && (
                <Button asChild className="min-h-[48px] text-base bg-[#7c2434] text-white hover:bg-[#651d2a]">
                  <Link to="/admin/gallery" data-testid="member-directory-gallery-staff-link">Gallery Staff — Review member artwork</Link>
                </Button>
              )}
              <Button variant="ghost" onClick={handleLogout} className="text-stone-600">
                <LogOut size={18} className="mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Directory */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="text-2xl font-serif text-stone-900">
                Member Directory
              </h2>
              
              <div className="flex items-center gap-3">
                {/* View Toggle */}
                <div className="flex border border-stone-300 rounded-md overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-cafa-red text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                    title="Card View"
                    data-testid="view-grid-btn"
                  >
                    <LayoutGrid size={18} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-cafa-red text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                    title="List View"
                    data-testid="view-list-btn"
                  >
                    <List size={18} />
                  </button>
                </div>
                
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                  <Input
                    placeholder="Search by name, city, or Instagram..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="member-search"
                  />
                </div>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="directory-filter-bar">
              {/* Type chips */}
              <div className="flex flex-wrap gap-1.5">
                {[
                  { value: 'all', label: 'All Types' },
                  { value: 'artist', label: 'Artists' },
                  { value: 'patron', label: 'Patrons' },
                  { value: 'instructor', label: 'Instructors' },
                  { value: 'board', label: 'Board' },
                  { value: 'lifetime', label: 'Lifetime' },
                  { value: 'general', label: 'General' },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setTypeFilter(value)}
                    data-testid={`type-filter-${value}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      typeFilter === value
                        ? 'bg-cafa-red text-white border-cafa-red'
                        : 'bg-white text-stone-600 border-stone-300 hover:border-cafa-red hover:text-cafa-red'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Separator */}
              {uniqueStates.length > 0 && <div className="h-4 border-l border-stone-300 mx-1" />}

              {/* Location filter */}
              {uniqueStates.length > 0 && (
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="px-2 py-1 text-xs border border-stone-300 rounded-full bg-white text-stone-600 focus:border-cafa-red focus:outline-none"
                  data-testid="location-filter-select"
                >
                  <option value="all">All Locations</option>
                  {uniqueStates.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}

              {/* Gallery toggle */}
              <button
                onClick={() => setHasGallery(!hasGallery)}
                data-testid="gallery-filter-toggle"
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1 ${
                  hasGallery
                    ? 'bg-cafa-red text-white border-cafa-red'
                    : 'bg-white text-stone-600 border-stone-300 hover:border-cafa-red hover:text-cafa-red'
                }`}
              >
                <Image size={12} /> Has Artwork
              </button>

              {/* Clear filters */}
              {(typeFilter !== 'all' || locationFilter !== 'all' || hasGallery || searchTerm) && (
                <button
                  onClick={() => { setTypeFilter('all'); setLocationFilter('all'); setHasGallery(false); setSearchTerm(''); }}
                  className="px-2 py-1 text-xs text-stone-400 hover:text-stone-700 underline"
                  data-testid="clear-filters-btn"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-stone-500">No members found</p>
            </div>
          ) : viewMode === 'grid' ? (
            /* Card/Grid View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredMembers.map((member) => (
                <Card key={member.id} className="border-stone-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center">
                      {member.photo_url ? (
                        <img
                          src={getImageUrl(member.photo_url)}
                          alt={`${member.first_name} ${member.last_name}`}
                          className="w-24 h-24 rounded-full object-cover mb-4"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-cafa-red-100 flex items-center justify-center mb-4">
                          <User size={40} className="text-cafa-red" />
                        </div>
                      )}
                      
                      <h3 className="text-lg font-semibold text-stone-900">
                        {member.first_name} {member.last_name}
                      </h3>
                      
                      {/* Membership Type Badge - Show Lifetime for lifetime members */}
                      {(() => {
                        // Check both new (is_lifetime_member) and legacy (lifetime_member) fields
                        const isLifetime = member.membership_status === 'lifetime' || member.is_lifetime_member === true || member.lifetime_member === true;
                        if (isLifetime) {
                          return (
                            <span className="text-xs px-2 py-0.5 rounded-full mt-1 bg-amber-100 text-amber-800">
                              Lifetime
                            </span>
                          );
                        }
                        if (member.membership_type) {
                          return (
                            <span className={`text-xs px-2 py-0.5 rounded-full mt-1 capitalize ${typeColors[member.membership_type] || typeColors.general}`}>
                              {member.membership_type}
                            </span>
                          );
                        }
                        return null;
                      })()}
                      
                      {member.position && (
                        <p className="text-sm text-cafa-red mt-1">{member.position}</p>
                      )}
                      
                      {(member.city || member.state) && (
                        <p className="text-sm text-stone-500 mt-1 flex items-center gap-1">
                          <MapPin size={14} />
                          {[member.city, member.state].filter(Boolean).join(', ')}
                        </p>
                      )}

                      {/* Gallery Preview */}
                      {member.gallery_preview && member.gallery_preview.length > 0 && (
                        <button
                          onClick={() => openGalleryModal(member)}
                          className="mt-4 w-full group"
                          data-testid={`gallery-preview-${member.id}`}
                        >
                          <div className="flex gap-1 justify-center">
                            {member.gallery_preview.slice(0, 4).map((img, idx) => (
                              <div 
                                key={img.id || idx} 
                                className="w-12 h-12 rounded overflow-hidden bg-stone-100 ring-1 ring-stone-200 group-hover:ring-cafa-red transition-all"
                              >
                                <img
                                  src={getImageUrl(img.url)}
                                  alt={img.title || 'Artwork'}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-stone-500 mt-1 group-hover:text-cafa-red transition-colors flex items-center justify-center gap-1">
                            <Image size={12} />
                            {member.gallery_count} artwork{member.gallery_count !== 1 ? 's' : ''}
                          </p>
                        </button>
                      )}
                      
                      <div className="flex gap-3 mt-4">
                        {member.website && (
                          <a
                            href={member.website.startsWith('http') ? member.website : `https://${member.website}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(member.website.startsWith('http') ? member.website : `https://${member.website}`, '_blank', 'noopener,noreferrer');
                            }}
                            className="p-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600 transition-colors cursor-pointer"
                            title="Website"
                          >
                            <Globe size={18} />
                          </a>
                        )}
                        {member.instagram && (
                          <a
                            href={`https://instagram.com/${member.instagram.replace('@', '')}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(`https://instagram.com/${member.instagram.replace('@', '')}`, '_blank', 'noopener,noreferrer');
                            }}
                            className="p-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600 transition-colors cursor-pointer"
                            title="Instagram"
                          >
                            <Instagram size={18} />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            /* Table/List View */
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto" ref={tableRef}>
                <table className="w-full" style={{ minWidth: '600px', tableLayout: 'fixed' }}>
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.name }}
                        onClick={() => handleSort('last_name')}
                      >
                        <div className="flex items-center gap-1">
                          Name {renderSortIcon('last_name')}
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('name', e)}
                        />
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.type }}
                        onClick={() => handleSort('membership_type')}
                      >
                        <div className="flex items-center gap-1">
                          Type {renderSortIcon('membership_type')}
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('type', e)}
                        />
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.location }}
                        onClick={() => handleSort('location')}
                      >
                        <div className="flex items-center gap-1">
                          Location {renderSortIcon('location')}
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('location', e)}
                        />
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider relative group"
                        style={{ width: columnWidths.gallery }}
                      >
                        <div className="flex items-center gap-1">
                          Gallery
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('gallery', e)}
                        />
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.website }}
                        onClick={() => handleSort('website')}
                      >
                        <div className="flex items-center gap-1">
                          Website {renderSortIcon('website')}
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('website', e)}
                        />
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 relative group"
                        style={{ width: columnWidths.instagram }}
                        onClick={() => handleSort('instagram')}
                      >
                        <div className="flex items-center gap-1">
                          Instagram {renderSortIcon('instagram')}
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-stone-300 opacity-0 group-hover:opacity-100 hover:bg-cafa-red"
                          onMouseDown={(e) => startResize('instagram', e)}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    {filteredMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3" style={{ width: columnWidths.name }}>
                          <div className="flex items-center gap-3 overflow-hidden">
                            {member.photo_url ? (
                              <img
                                src={member.photo_url}
                                alt={`${member.first_name} ${member.last_name}`}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-cafa-red-100 flex items-center justify-center flex-shrink-0">
                                <User size={20} className="text-cafa-red" />
                              </div>
                            )}
                            <span className="font-medium text-stone-900 truncate">
                              {member.first_name} {member.last_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ width: columnWidths.type }}>
                          {(() => {
                            // Show "Lifetime" for lifetime members, otherwise show membership_type
                            // Check both new (is_lifetime_member) and legacy (lifetime_member) fields
                            const isLifetime = member.membership_status === 'lifetime' || member.is_lifetime_member === true || member.lifetime_member === true;
                            if (isLifetime) {
                              return (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                  Lifetime
                                </span>
                              );
                            }
                            return (
                              <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${typeColors[member.membership_type] || typeColors.general}`}>
                                {member.membership_type || 'general'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3" style={{ width: columnWidths.location }}>
                          <span className="text-sm text-stone-600 truncate block">
                            {[member.city, member.state].filter(Boolean).join(', ') || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3" style={{ width: columnWidths.gallery }}>
                          {member.gallery_count > 0 ? (
                            <button
                              onClick={() => openGalleryModal(member)}
                              className="flex items-center gap-1 text-sm text-cafa-red hover:underline"
                            >
                              <Image size={14} />
                              {member.gallery_count}
                            </button>
                          ) : (
                            <span className="text-stone-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3" style={{ width: columnWidths.website }}>
                          {member.website ? (
                            <a
                              href={member.website.startsWith('http') ? member.website : `https://${member.website}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(member.website.startsWith('http') ? member.website : `https://${member.website}`, '_blank', 'noopener,noreferrer');
                              }}
                              className="text-sm text-cafa-blue hover:underline truncate block cursor-pointer"
                              title={member.website}
                            >
                              {member.website.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                            </a>
                          ) : (
                            <span className="text-stone-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3" style={{ width: columnWidths.instagram }}>
                          {member.instagram ? (
                            <a
                              href={`https://instagram.com/${member.instagram.replace('@', '')}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(`https://instagram.com/${member.instagram.replace('@', '')}`, '_blank', 'noopener,noreferrer');
                              }}
                              className="text-sm text-cafa-blue hover:underline truncate block cursor-pointer"
                              title={member.instagram}
                            >
                              @{member.instagram.replace('@', '')}
                            </a>
                          ) : (
                            <span className="text-stone-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Member Count */}
          <div className="mt-6 text-center text-sm text-stone-500">
            Showing {filteredMembers.length} of {members.length} active and lifetime member{members.length !== 1 ? 's' : ''}
            {(typeFilter !== 'all' || locationFilter !== 'all' || hasGallery || searchTerm) && (
              <span className="ml-1 text-cafa-red">— filtered</span>
            )}
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      {galleryModal && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => { setGalleryModal(null); setViewingImage(null); }}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <div className="flex items-center gap-3">
                {galleryModal.photo_url ? (
                  <img
                    src={getImageUrl(galleryModal.photo_url)}
                    alt={`${galleryModal.first_name} ${galleryModal.last_name}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-cafa-red-100 flex items-center justify-center">
                    <User size={24} className="text-cafa-red" />
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-semibold text-stone-900">
                    {galleryModal.first_name} {galleryModal.last_name}
                  </h2>
                  <p className="text-sm text-stone-500">Art Gallery</p>
                </div>
              </div>
              <button
                onClick={() => { setGalleryModal(null); setViewingImage(null); }}
                className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              >
                <X size={20} className="text-stone-600" />
              </button>
            </div>

            {/* Gallery Grid */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {galleryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
                </div>
              ) : fullGallery.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {fullGallery.map((image) => (
                    <button
                      key={image.id}
                      onClick={() => setViewingImage(image)}
                      className="group aspect-square rounded-lg overflow-hidden bg-stone-100 ring-1 ring-stone-200 hover:ring-2 hover:ring-cafa-red transition-all"
                    >
                      <img
                        src={getImageUrl(image.url)}
                        alt={image.title || 'Artwork'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      {image.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-sm font-medium truncate">{image.title}</p>
                          {image.medium && <p className="text-white/70 text-xs">{image.medium}</p>}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Image size={48} className="mx-auto mb-4 text-stone-300" />
                  <p className="text-stone-500">No artwork to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-screen Image Viewer */}
      {viewingImage && (
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-[60]"
          onClick={() => setViewingImage(null)}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"
            onClick={() => setViewingImage(null)}
          >
            <X size={32} />
          </button>

          {/* Navigation */}
          {fullGallery.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
                onClick={(e) => { e.stopPropagation(); navigateViewer('prev'); }}
              >
                <ChevronLeft size={48} />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
                onClick={(e) => { e.stopPropagation(); navigateViewer('next'); }}
              >
                <ChevronRight size={48} />
              </button>
            </>
          )}

          {/* Image */}
          <div className="max-w-5xl max-h-[85vh] px-16" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(viewingImage.url)}
              alt={viewingImage.title || 'Artwork'}
              className="max-w-full max-h-[75vh] object-contain mx-auto"
            />
            
            {/* Caption */}
            {(viewingImage.title || viewingImage.medium || viewingImage.year) && (
              <div className="text-center mt-4 text-white">
                {viewingImage.title && (
                  <h3 className="text-xl font-medium">{viewingImage.title}</h3>
                )}
                <p className="text-white/70 text-sm mt-1">
                  {[viewingImage.medium, viewingImage.year].filter(Boolean).join(' • ')}
                </p>
                {viewingImage.description && (
                  <p className="text-white/60 text-sm mt-2 max-w-2xl mx-auto">
                    {viewingImage.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
